/**
 * The edge hash chain — API_CONTRACT_SV_v0.1 §2, "Chain rule (svapp + be must agree)":
 *
 *   h_0 = SHA-256(nonce)
 *   h_i = SHA-256(h_{i-1} ‖ SHA-256(c_i) ‖ nonce ‖ capturedAt_i ‖ geo_i ‖ imu_i ‖ promptId_i)
 *
 * where ‖ is byte concatenation of UTF-8 strings with `\n` separators, every hash is LOWERCASE HEX
 * (so h_{i-1} and SHA-256(c_i) enter the pre-image as their 64-char hex strings), and geo_i / imu_i
 * are the EXACT header strings sent on the chunk (the empty string when the header is omitted).
 *
 * Pinned by the be lane's vectors (src/capture/__tests__/chainVectors.json is a VERBATIM copy of
 * vCIP_be_4.0/src/test/resources/sv/chain-vectors.json — never edited here):
 *   • one chain PER SHOT — h_0 restarts from the visit nonce for every shotId;
 *   • `seq` is 0-BASED: the chunk at seq=i carries h_{i+1} (seq 0 links from h_0);
 *     `finalChainHash` = h_n; `chunkCount` = n;
 *   • `promptId_i` = the script item's `shotId` (an OPAQUE, URL-safe string, e.g. `EXT_BOARD_STREET.1`), sent verbatim as
 *     X-SV-Prompt-Id — the be refuses a chunk whose header does not equal the shot's own id;
 *   • `capturedAt_i` = the ISO-8601 UTC string sent as X-SV-Captured-At, verbatim.
 *
 * Honest limit (FSD §4.2): the chain proves that what the server received is what this client
 * sent, in order, bound to nonce / time / geo. It does not prove the pixels came from a sensor.
 * WebCrypto only — no bundled hash library on the representative's critical path.
 */

export const CHAIN_SEPARATOR = '\n';

const encoder = new TextEncoder();

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c || !c.subtle) {
    throw new Error('WebCrypto (crypto.subtle) is unavailable — the hash chain cannot be built');
  }
  return c.subtle;
}

export function bytesToHex(bytes: ArrayBuffer | Uint8Array): string {
  const view = ArrayBuffer.isView(bytes) ? (bytes as Uint8Array) : new Uint8Array(bytes as ArrayBuffer);
  let out = '';
  for (let i = 0; i < view.length; i += 1) {
    out += (view[i] as number).toString(16).padStart(2, '0');
  }
  return out;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) throw new Error('not a hex string');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** SHA-256 of raw bytes or of a string's UTF-8 encoding, as lowercase hex. */
export async function sha256Hex(input: Uint8Array | string): Promise<string> {
  const bytes = typeof input === 'string' ? encoder.encode(input) : input;
  const digest = await subtle().digest('SHA-256', bytes as BufferSource);
  return bytesToHex(digest);
}

/** h_0 */
export function chainGenesis(nonce: string): Promise<string> {
  return sha256Hex(nonce);
}

export interface ChainLinkInput {
  readonly prev: string;
  readonly chunkSha256: string;
  readonly nonce: string;
  readonly capturedAt: string;
  readonly geo: string;
  readonly imu: string;
  readonly promptId: string;
}

/** The exact UTF-8 pre-image string for h_i — exported so the be lane can diff pre-images. */
export function chainPreimage(i: ChainLinkInput): string {
  return [i.prev, i.chunkSha256, i.nonce, i.capturedAt, i.geo, i.imu, i.promptId].join(
    CHAIN_SEPARATOR,
  );
}

/** h_i */
export function chainLink(i: ChainLinkInput): Promise<string> {
  return sha256Hex(chainPreimage(i));
}

export interface ChunkLink {
  /** 0-based — the PUT path `/chunks/{seq}` value for this chunk. */
  readonly seq: number;
  readonly chunkSha256: string;
  readonly chainHash: string;
}

export interface ChunkBinding {
  readonly capturedAt: string;
  /** Exact header string, or null when omitted (enters the pre-image as ''). */
  readonly geo: string | null;
  readonly imu: string | null;
}

/**
 * A per-shot chain builder. `append` is serialised internally: chunks arriving from MediaRecorder
 * are hashed strictly in order even when the caller does not await each call.
 */
export class ShotChain {
  private head: string | null = null;
  private n = 0;
  private tail: Promise<unknown> = Promise.resolve();

  constructor(
    public readonly nonce: string,
    public readonly promptId: string,
  ) {}

  get count(): number {
    return this.n;
  }

  /** The current head (h_n), or h_0 after `genesis()`. Null before genesis. */
  get headHash(): string | null {
    return this.head;
  }

  async genesis(): Promise<string> {
    this.head = await chainGenesis(this.nonce);
    this.n = 0;
    return this.head;
  }

  append(bytes: Uint8Array, binding: ChunkBinding): Promise<ChunkLink> {
    const run = this.tail.then(async () => {
      if (this.head === null) await this.genesis();
      const chunkSha256 = await sha256Hex(bytes);
      const chainHash = await chainLink({
        prev: this.head as string,
        chunkSha256,
        nonce: this.nonce,
        capturedAt: binding.capturedAt,
        geo: binding.geo ?? '',
        imu: binding.imu ?? '',
        promptId: this.promptId,
      });
      this.head = chainHash;
      this.n += 1;
      return { seq: this.n - 1, chunkSha256, chainHash };
    });
    this.tail = run.catch(() => undefined);
    return run;
  }
}

/**
 * Server-side recompute helper (used by the mock adapter, and the reference for the be lane):
 * given the previous head and the received chunk + its headers, the hash the server EXPECTS.
 */
export async function recomputeLink(
  prev: string,
  bytes: Uint8Array,
  nonce: string,
  headers: { capturedAt: string; geo: string | null; imu: string | null; promptId: string },
): Promise<string> {
  return chainLink({
    prev,
    chunkSha256: await sha256Hex(bytes),
    nonce,
    capturedAt: headers.capturedAt,
    geo: headers.geo ?? '',
    imu: headers.imu ?? '',
    promptId: headers.promptId,
  });
}
